import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Tabs, Descriptions, Tag, Button, Space, Timeline, Table, Modal, Form, Input, InputNumber, Select, DatePicker, message, Popconfirm, Switch, Row, Col, Statistic
} from 'antd';
import {
  ArrowLeftOutlined, HistoryOutlined, DollarOutlined, TeamOutlined, PlusOutlined, EditOutlined, DeleteOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { firmsApi } from '../api/firms';
import type { FirmHistory, FirmFinancial, CreateFinancialRequest, FirmRole } from '../types';
import { MaskedValue } from '../components/MaskedValue';

export function FirmDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const firmId = parseInt(id || '0');
  const queryClient = useQueryClient();

  const [isFinancialModalOpen, setIsFinancialModalOpen] = useState(false);
  const [editingFinancial, setEditingFinancial] = useState<FirmFinancial | null>(null);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<FirmRole | null>(null);

  const [financialForm] = Form.useForm();
  const [roleForm] = Form.useForm();

  const { data: firm, isLoading: firmLoading } = useQuery({
    queryKey: ['firm', firmId],
    queryFn: () => firmsApi.getById(firmId),
    enabled: !!firmId,
  });

  const { data: history = [] } = useQuery({
    queryKey: ['firm-history', firmId],
    queryFn: () => firmsApi.getHistory(firmId),
    enabled: !!firmId,
  });

  const { data: financials = [] } = useQuery({
    queryKey: ['firm-financials', firmId],
    queryFn: () => firmsApi.getFinancials(firmId),
    enabled: !!firmId,
  });

  const { data: financialSummary } = useQuery({
    queryKey: ['firm-financial-summary', firmId],
    queryFn: () => firmsApi.getFinancialSummary(firmId),
    enabled: !!firmId,
  });

  const { data: roles = [] } = useQuery({
    queryKey: ['firm-roles', firmId],
    queryFn: () => firmsApi.getRoles(firmId),
    enabled: !!firmId,
  });

  // Financial mutations
  const addFinancialMutation = useMutation({
    mutationFn: (data: CreateFinancialRequest) => firmsApi.addFinancial(firmId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firm-financials', firmId] });
      queryClient.invalidateQueries({ queryKey: ['firm-financial-summary', firmId] });
      message.success('İşlem eklendi');
      setIsFinancialModalOpen(false);
      financialForm.resetFields();
    },
    onError: () => message.error('İşlem eklenemedi'),
  });

  const updateFinancialMutation = useMutation({
    mutationFn: ({ fid, data }: { fid: number; data: CreateFinancialRequest }) => firmsApi.updateFinancial(firmId, fid, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firm-financials', firmId] });
      queryClient.invalidateQueries({ queryKey: ['firm-financial-summary', firmId] });
      message.success('İşlem güncellendi');
      setIsFinancialModalOpen(false);
      setEditingFinancial(null);
      financialForm.resetFields();
    },
    onError: () => message.error('İşlem güncellenemedi'),
  });

  const deleteFinancialMutation = useMutation({
    mutationFn: (fid: number) => firmsApi.deleteFinancial(firmId, fid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firm-financials', firmId] });
      queryClient.invalidateQueries({ queryKey: ['firm-financial-summary', firmId] });
      message.success('İşlem silindi');
    },
    onError: () => message.error('İşlem silinemedi'),
  });

  // Role mutations
  const createRoleMutation = useMutation({
    mutationFn: (data: any) => firmsApi.createRole(firmId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firm-roles', firmId] });
      message.success('Rol oluşturuldu');
      setIsRoleModalOpen(false);
      roleForm.resetFields();
    },
    onError: (error: any) => message.error(error.response?.data?.message || 'Rol oluşturulamadı'),
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ roleId, data }: { roleId: number; data: any }) => firmsApi.updateRole(firmId, roleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firm-roles', firmId] });
      message.success('Rol güncellendi');
      setIsRoleModalOpen(false);
      setEditingRole(null);
      roleForm.resetFields();
    },
    onError: (error: any) => message.error(error.response?.data?.message || 'Rol güncellenemedi'),
  });

  const deleteRoleMutation = useMutation({
    mutationFn: (roleId: number) => firmsApi.deleteRole(firmId, roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firm-roles', firmId] });
      message.success('Rol silindi');
    },
    onError: (error: any) => message.error(error.response?.data?.message || 'Rol silinemedi'),
  });

  const handleFinancialSubmit = (values: any) => {
    const data: CreateFinancialRequest = {
      transactionType: values.transactionType,
      description: values.description,
      amount: values.amount,
      currency: values.currency || 'TRY',
      periodStart: values.period?.[0]?.format('YYYY-MM-DD'),
      periodEnd: values.period?.[1]?.format('YYYY-MM-DD'),
      notes: values.notes,
    };

    if (editingFinancial) {
      updateFinancialMutation.mutate({ fid: editingFinancial.id, data });
    } else {
      addFinancialMutation.mutate(data);
    }
  };

  const handleRoleSubmit = (values: any) => {
    if (editingRole) {
      updateRoleMutation.mutate({ roleId: editingRole.id, data: values });
    } else {
      createRoleMutation.mutate(values);
    }
  };

  const getLicenseStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green';
      case 'paused': return 'orange';
      case 'expired': return 'red';
      case 'deactivated': return 'default';
      default: return 'default';
    }
  };

  const getLicenseStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Aktif';
      case 'paused': return 'Duraklatıldı';
      case 'expired': return 'Süresi Doldu';
      case 'deactivated': return 'Devre Dışı';
      default: return status;
    }
  };

  const getActionTypeIcon = (actionType: string) => {
    switch (actionType) {
      case 'created': return 'green';
      case 'license_renewed': return 'blue';
      case 'license_changed': return 'orange';
      case 'paused': return 'orange';
      case 'activated': return 'green';
      case 'deactivated': return 'red';
      case 'dealer_changed': return 'purple';
      default: return 'gray';
    }
  };

  const getTransactionTypeText = (type: string) => {
    switch (type) {
      case 'annual_fee': return 'Yıllık Ücret';
      case 'addon': return 'Ek Satış';
      case 'discount': return 'İndirim';
      case 'payment': return 'Ödeme';
      default: return type;
    }
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'annual_fee': return 'blue';
      case 'addon': return 'green';
      case 'discount': return 'orange';
      case 'payment': return 'purple';
      default: return 'default';
    }
  };

  if (firmLoading || !firm) {
    return <Card loading />;
  }

  const financialColumns = [
    {
      title: 'Tarih',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY'),
    },
    {
      title: 'Tip',
      dataIndex: 'transactionType',
      key: 'transactionType',
      render: (type: string) => (
        <Tag color={getTransactionTypeColor(type)}>{getTransactionTypeText(type)}</Tag>
      ),
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Tutar',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: FirmFinancial) => (
        <span style={{ color: record.transactionType === 'discount' ? 'red' : 'inherit' }}>
          {record.transactionType === 'discount' ? '-' : ''}
          {amount.toLocaleString('tr-TR', { style: 'currency', currency: record.currency })}
        </span>
      ),
    },
    {
      title: 'Dönem',
      key: 'period',
      render: (_: unknown, record: FirmFinancial) =>
        record.periodStart && record.periodEnd
          ? `${dayjs(record.periodStart).format('DD.MM.YYYY')} - ${dayjs(record.periodEnd).format('DD.MM.YYYY')}`
          : '-',
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: unknown, record: FirmFinancial) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingFinancial(record);
              financialForm.setFieldsValue({
                ...record,
                period: record.periodStart && record.periodEnd
                  ? [dayjs(record.periodStart), dayjs(record.periodEnd)]
                  : undefined,
              });
              setIsFinancialModalOpen(true);
            }}
          />
          <Popconfirm
            title="İşlemi silmek istediğinize emin misiniz?"
            onConfirm={() => deleteFinancialMutation.mutate(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const roleColumns = [
    {
      title: 'Rol Adı',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: FirmRole) => (
        <Space>
          {name}
          {record.isDefault && <Tag color="blue">Varsayılan</Tag>}
        </Space>
      ),
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Kullanıcı Sayısı',
      dataIndex: 'userCount',
      key: 'userCount',
      render: (count: number) => <Tag icon={<TeamOutlined />}>{count}</Tag>,
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: unknown, record: FirmRole) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingRole(record);
              roleForm.setFieldsValue(record);
              setIsRoleModalOpen(true);
            }}
          />
          <Popconfirm
            title="Rolü silmek istediğinize emin misiniz?"
            onConfirm={() => deleteRoleMutation.mutate(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'info',
      label: 'Genel Bilgiler',
      children: (
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Firma Adı">{firm.name}</Descriptions.Item>
          <Descriptions.Item label="Resmi Adı">{firm.officialName || '-'}</Descriptions.Item>
          <Descriptions.Item label="Subdomain">
            <code>{firm.subdomain}.raporgor.com</code>
          </Descriptions.Item>
          <Descriptions.Item label="Lisans Anahtarı">
            <MaskedValue value={firm.licenseKey} />
          </Descriptions.Item>
          <Descriptions.Item label="Lisans Durumu">
            <Tag color={getLicenseStatusColor(firm.licenseStatus)}>
              {getLicenseStatusText(firm.licenseStatus)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Lisans Bitiş">
            {firm.licenseExpiresAt ? dayjs(firm.licenseExpiresAt).format('DD.MM.YYYY') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Bayi">{firm.dealerName || 'Doğrudan Müşteri'}</Descriptions.Item>
          <Descriptions.Item label="İletişim E-posta">{firm.contactEmail || '-'}</Descriptions.Item>
          <Descriptions.Item label="Telefon">{firm.phone || '-'}</Descriptions.Item>
          <Descriptions.Item label="Vergi No">{firm.taxNumber || '-'}</Descriptions.Item>
          <Descriptions.Item label="Vergi Dairesi">{firm.taxOffice || '-'}</Descriptions.Item>
          <Descriptions.Item label="Şehir/İlçe">
            {[firm.city, firm.district].filter(Boolean).join(' / ') || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Adres" span={2}>{firm.address || '-'}</Descriptions.Item>
          <Descriptions.Item label="Notlar" span={2}>{firm.notes || '-'}</Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'history',
      label: (
        <span>
          <HistoryOutlined /> Tarihçe
        </span>
      ),
      children: (
        <Timeline
          items={history.map((item: FirmHistory) => ({
            color: getActionTypeIcon(item.actionType),
            children: (
              <div>
                <div style={{ fontWeight: 500 }}>{item.description}</div>
                <div style={{ color: '#888', fontSize: '12px' }}>
                  {dayjs(item.createdAt).format('DD.MM.YYYY HH:mm')}
                  {item.performedBy && ` - ${item.performedBy}`}
                </div>
              </div>
            ),
          }))}
        />
      ),
    },
    {
      key: 'financials',
      label: (
        <span>
          <DollarOutlined /> Finansal
        </span>
      ),
      children: (
        <>
          {financialSummary && (
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Toplam Gelir"
                    value={financialSummary.totalRevenue}
                    precision={2}
                    suffix="TL"
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Yıllık Ücretler"
                    value={financialSummary.totalAnnualFees}
                    precision={2}
                    suffix="TL"
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Ek Satışlar"
                    value={financialSummary.totalAddons}
                    precision={2}
                    suffix="TL"
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="İndirimler"
                    value={financialSummary.totalDiscounts}
                    precision={2}
                    suffix="TL"
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Card>
              </Col>
            </Row>
          )}
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingFinancial(null);
                financialForm.resetFields();
                financialForm.setFieldsValue({ currency: 'TRY' });
                setIsFinancialModalOpen(true);
              }}
            >
              Yeni İşlem
            </Button>
          </div>
          <Table columns={financialColumns} dataSource={financials} rowKey="id" />
        </>
      ),
    },
    {
      key: 'roles',
      label: (
        <span>
          <TeamOutlined /> Roller
        </span>
      ),
      children: (
        <>
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingRole(null);
                roleForm.resetFields();
                setIsRoleModalOpen(true);
              }}
            >
              Yeni Rol
            </Button>
          </div>
          <Table columns={roleColumns} dataSource={roles} rowKey="id" />
        </>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/firms')}>
          Geri
        </Button>
      </div>

      <Card title={firm.name}>
        <Tabs items={tabItems} />
      </Card>

      {/* Financial Modal */}
      <Modal
        title={editingFinancial ? 'İşlem Düzenle' : 'Yeni İşlem'}
        open={isFinancialModalOpen}
        onCancel={() => {
          setIsFinancialModalOpen(false);
          setEditingFinancial(null);
          financialForm.resetFields();
        }}
        footer={null}
      >
        <Form form={financialForm} layout="vertical" onFinish={handleFinancialSubmit}>
          <Form.Item
            name="transactionType"
            label="İşlem Tipi"
            rules={[{ required: true, message: 'İşlem tipi gerekli' }]}
          >
            <Select>
              <Select.Option value="annual_fee">Yıllık Ücret</Select.Option>
              <Select.Option value="addon">Ek Satış</Select.Option>
              <Select.Option value="discount">İndirim</Select.Option>
              <Select.Option value="payment">Ödeme</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Açıklama"
            rules={[{ required: true, message: 'Açıklama gerekli' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="amount"
            label="Tutar"
            rules={[{ required: true, message: 'Tutar gerekli' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="currency" label="Para Birimi">
            <Select>
              <Select.Option value="TRY">TRY</Select.Option>
              <Select.Option value="USD">USD</Select.Option>
              <Select.Option value="EUR">EUR</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="period" label="Dönem">
            <DatePicker.RangePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="notes" label="Notlar">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={addFinancialMutation.isPending || updateFinancialMutation.isPending}>
                {editingFinancial ? 'Güncelle' : 'Ekle'}
              </Button>
              <Button onClick={() => setIsFinancialModalOpen(false)}>İptal</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Role Modal */}
      <Modal
        title={editingRole ? 'Rol Düzenle' : 'Yeni Rol'}
        open={isRoleModalOpen}
        onCancel={() => {
          setIsRoleModalOpen(false);
          setEditingRole(null);
          roleForm.resetFields();
        }}
        footer={null}
      >
        <Form form={roleForm} layout="vertical" onFinish={handleRoleSubmit}>
          <Form.Item
            name="name"
            label="Rol Adı"
            rules={[{ required: true, message: 'Rol adı gerekli' }]}
            extra="Rol adı, müşteri panelindeki izin yapılandırmasıyla eşleşmelidir (örn: Admin, Muhasebe, Satış)"
          >
            <Input placeholder="Örn: Admin, Muhasebe, Satış, Depo" />
          </Form.Item>

          <Form.Item name="description" label="Açıklama">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item name="isDefault" label="Varsayılan Rol" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={createRoleMutation.isPending || updateRoleMutation.isPending}>
                {editingRole ? 'Güncelle' : 'Oluştur'}
              </Button>
              <Button onClick={() => setIsRoleModalOpen(false)}>İptal</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
